/**
 * Jarvis Tracking - Lightweight event hook layer for CRO automation
 * 
 * This script attaches click listeners to all elements with data-jarvis-event
 * and dispatches custom events that Jarvis can hook into for automated CRO,
 * UTM hygiene, and pixel-ready tracking.
 * 
 * No external network calls - this is just the hook layer.
 */

(function() {
  'use strict';

  // Get theme version from global variable (set in theme.liquid)
  const themeVersion = window.JARVIS_THEME_VERSION || 'unknown';
  const SUBSCRIPTION_EVENT_URL = window.JARVIS_SUBSCRIPTION_EVENT_URL || 'https://subscriptions.velunasleep.com/events/ingest';
  const SUBSCRIPTION_SESSION_KEY = 'jarvis_subscription_intent';
  const SUBSCRIPTION_SESSION_ID_KEY = 'jarvis_subscription_session_id';
  const SUBSCRIPTION_EVENT_ALLOWLIST = new Set([
    'subscription_selected',
    'pdp_subscription_toggle',
    'pdp_add_to_cart',
    'checkout_start',
    'account_subscription_manage',
    'account_subscription_start',
    'post_purchase_offer_viewed',
    'post_purchase_offer_accepted'
  ]);

  function getSessionId() {
    try {
      let existing = sessionStorage.getItem(SUBSCRIPTION_SESSION_ID_KEY);
      if (existing) return existing;
      existing = 'subsess_' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
      sessionStorage.setItem(SUBSCRIPTION_SESSION_ID_KEY, existing);
      return existing;
    } catch (err) {
      return 'subsess_fallback';
    }
  }

  function getSubscriptionIntent() {
    try {
      const raw = sessionStorage.getItem(SUBSCRIPTION_SESSION_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (err) {
      return {};
    }
  }

  function setSubscriptionIntent(nextState) {
    try {
      const current = getSubscriptionIntent();
      const merged = Object.assign({}, current, nextState || {});
      sessionStorage.setItem(SUBSCRIPTION_SESSION_KEY, JSON.stringify(merged));
      return merged;
    } catch (err) {
      return nextState || {};
    }
  }

  function inferCadenceMonths(text) {
    const match = String(text || '').match(/(\d+)\s*[- ]?month/i);
    if (match) return parseInt(match[1], 10);
    if (/monthly|every month/i.test(String(text || ''))) return 1;
    return null;
  }

  function buildTouchpointPayload(element, eventName, location) {
    const dataset = element.dataset || {};
    let intent = getSubscriptionIntent();
    const payload = {
      event_name: eventName,
      source_app: 'storefront',
      source_surface: location || 'unknown',
      theme_version: themeVersion,
      session_id: getSessionId(),
      url: window.location.pathname,
      product_handle: dataset.jarvisProductHandle || intent.product_handle || null,
      ritual_handle: dataset.jarvisRitual || intent.ritual_handle || dataset.jarvisProductHandle || null,
      bundle_tier: dataset.jarvisBundleType || intent.bundle_tier || null,
      cadence_months: Number(dataset.jarvisCadenceMonths || intent.cadence_months || 0) || null,
      plan_name: dataset.jarvisPlanName || intent.plan_name || null,
      element: {
        id: element.id || null,
        tagName: element.tagName || null,
        text: element.textContent ? element.textContent.trim().substring(0, 100) : null
      }
    };

    if (eventName === 'subscription_selected' || eventName === 'pdp_subscription_toggle') {
      const selected = eventName === 'subscription_selected' ? true : !!element.checked;
      if (!selected) {
        setSubscriptionIntent({ selected: false });
        return null;
      }
      const inferredCadence = payload.cadence_months || inferCadenceMonths(element.textContent);
      intent = setSubscriptionIntent({
        selected: true,
        product_handle: payload.product_handle,
        ritual_handle: payload.ritual_handle,
        bundle_tier: payload.bundle_tier,
        cadence_months: inferredCadence,
        plan_name: payload.plan_name || (element.textContent ? element.textContent.trim().substring(0, 80) : null)
      });
      payload.cadence_months = intent.cadence_months || inferredCadence || null;
      payload.plan_name = intent.plan_name || payload.plan_name;
      return payload;
    }

    if (eventName === 'pdp_add_to_cart' || eventName === 'checkout_start') {
      if (!intent.selected) {
        return null;
      }
      payload.product_handle = payload.product_handle || intent.product_handle || null;
      payload.ritual_handle = payload.ritual_handle || intent.ritual_handle || payload.product_handle || null;
      payload.bundle_tier = payload.bundle_tier || intent.bundle_tier || null;
      payload.cadence_months = payload.cadence_months || intent.cadence_months || null;
      payload.plan_name = payload.plan_name || intent.plan_name || null;
      return payload;
    }

    return payload;
  }

  function postSubscriptionTouchpoint(payload) {
    /* 2026-09-11: subscriptions.velunasleep.com/events/ingest was retired 2026-09-05; no network call. */
    return;
    if (!payload || !payload.event_name || !SUBSCRIPTION_EVENT_ALLOWLIST.has(payload.event_name)) {
      return;
    }

    const body = JSON.stringify(payload);
    try {
      fetch(SUBSCRIPTION_EVENT_URL, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: body
      }).catch(function() {});
    } catch (err) {
      // Non-blocking analytics only.
    }
  }

  /**
   * Dispatch jarvis:event custom event
   */
  function dispatchJarvisEvent(element, eventName, location) {
    const eventDetail = {
      event: eventName,
      location: location,
      themeVersion: themeVersion,
      timestamp: new Date().toISOString(),
      element: {
        tagName: element.tagName,
        id: element.id || null,
        className: element.className || null,
        href: element.href || null,
        text: element.textContent ? element.textContent.trim().substring(0, 100) : null
      }
    };

    // Dispatch custom event
    const customEvent = new CustomEvent('jarvis:event', {
      detail: eventDetail,
      bubbles: true,
      cancelable: true
    });

    element.dispatchEvent(customEvent);

    // Also dispatch to window for global listeners
    window.dispatchEvent(customEvent);

    // Forward subscription-relevant touchpoints to the event bus.
    postSubscriptionTouchpoint(buildTouchpointPayload(element, eventName, location));
  }

  /**
   * Attach click listeners to all elements with data-jarvis-event
   */
  function attachJarvisListeners() {
    // Find all elements with data-jarvis-event attribute
    const elements = document.querySelectorAll('[data-jarvis-event]');

    elements.forEach(function(element) {
      // Skip if already has listener attached
      if (element.hasAttribute('data-jarvis-listener-attached')) {
        return;
      }

      // Mark as having listener attached
      element.setAttribute('data-jarvis-listener-attached', 'true');

      // Get event name and location from data attributes
      const eventName = element.getAttribute('data-jarvis-event');
      const location = element.getAttribute('data-jarvis-location') || 'unknown';

      // Attach click listener
      element.addEventListener('click', function(e) {
        dispatchJarvisEvent(element, eventName, location);
      }, { passive: true });
    });
  }

  /**
   * Initialize tracking when DOM is ready
   */
  function init() {
    // Attach listeners immediately for elements already in DOM
    attachJarvisListeners();

    // Also watch for dynamically added elements (e.g., AJAX-loaded content)
    // Use MutationObserver to detect new elements with data-jarvis-event
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(function(mutations) {
        let shouldReattach = false;
        
        mutations.forEach(function(mutation) {
          if (mutation.addedNodes.length > 0) {
            shouldReattach = true;
          }
        });

        if (shouldReattach) {
          attachJarvisListeners();
        }
      });

      // Start observing
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded
    init();
  }

  // Expose utility function for manual event dispatching (if needed)
  window.jarvisTrack = function(eventName, location, element) {
    const targetElement = element || document.body;
    dispatchJarvisEvent(targetElement, eventName, location);
  };

})();

/* ===== VELUNA_QUIZ_FUNNEL_BEGIN (managed by embed_quiz_funnel_theme.py) ===== */
;(function(){ if(window.__VELUNA_QUIZ_FUNNEL__){return;} window.__VELUNA_QUIZ_FUNNEL__=1;
try{
/* OWNED TIKTOK PIXEL D9DRAERC77UAEPV3J3N0 SILENCED 2026-09-11 — decision gate: the Shopify partner pixel (D2H46MRC77U0DDF71UUG) is Veluna's canonical TikTok dataset. No TikTok SDK is loaded here and no ttq event leaves this file; first-party attribution capture (vlnAttr), consent handling and the Meta quiz block are unchanged. Backup: data/theme_backups/20260911_owned_pixel_silence/ */
var ttq={page:function(){},track:function(){},identify:function(){},load:function(){},instance:function(){return ttq;}};
(function(w,d){
 function cur(){try{return (w.Shopify&&Shopify.currency&&Shopify.currency.active)||((w.ShopifyAnalytics||{}).meta||{}).currency||'USD'}catch(e){return 'USD'}}
 function meta(){try{return (w.ShopifyAnalytics||{}).meta||{}}catch(e){return {}}}
 function refreshCart(){try{fetch('/cart.js',{credentials:'same-origin'}).then(function(r){return r.json()}).then(function(c){w.__ttCart=c}).catch(function(){})}catch(e){}}
 refreshCart();
 var path=location.pathname;
 // ATTRIBUTION CAPTURE — TWO EXPLICIT CONCEPTS.
 //
 // ORIGINAL ACQUISITION ("what acquired this person") is written once and is then
 // effectively immutable. Only a genuine NEW acquisition touch may replace it: a
 // click that carries a campaign identifier AND is not an owned-channel email
 // return. Merging was rejected for the original reason - an old creative's
 // utm_content riding along with a new click's ttclid attributes the order to the
 // wrong ad - so a real new ad click still REPLACES the acquisition set wholesale.
 //
 // RETURN TOUCH ("what brought them back to convert") is a separate namespace and
 // is the ONLY thing a lifecycle email may write. Before this, an email click
 // overwrote utm_source/campaign/content and dropped vln_exp + vln_path, which
 // both destroyed paid acquisition truth and broke the utm_campaign == vln_exp
 // invariant the reconciliation depends on.
 //
 // vln_test rides the acquisition path so a QA purchase is delivered to TikTok
 // (proving the pipeline) while being excluded from every commercial result.
 var VLN_KEYS=['utm_source','utm_medium','utm_campaign','utm_content','utm_term','vln_exp','vln_path','vln_test'];
 var VLN_TOUCH=['ttclid','vln_exp','utm_campaign','utm_content','utm_source'];
 var ACQ_LS='__vln_attr';        // unchanged key: acquisition already stored on devices survives
 var RET_LS='__vln_return';
 function qpGet(qp,k){ var v=qp.get(k); return v?String(v).slice(0,120):''; }
 // An owned-channel return touch. Covers email (canonical form plus the legacy
 // utm_source=email/medium=ses the engine emitted before the attribution gate)
 // and owned content assets such as the downloadable sleep guides.
 //
 // This MUST stay the only gate into the return-touch branch: anything it does
 // not recognise falls through to the acquisition branch and replaces the
 // acquisition set. A guide CTA is a re-engagement of someone we already
 // acquired, so it belongs here, never in acquisition.
 function ownedTouchPath(qp){
   var s=(qpGet(qp,'utm_source')||'').toLowerCase();
   var m=(qpGet(qp,'utm_medium')||'').toLowerCase();
   var p=(qpGet(qp,'vln_path')||'').toLowerCase();
   if(p==='email' || m==='email' || m==='ses' || s==='veluna_email' || s==='email') return 'email';
   if(p==='guide' || m==='pdf' || s==='veluna_guide') return 'guide';
   return '';
 }
 // Back-compat alias; some callers/tests reference the original name.
 function isEmailTouch(qp){ return ownedTouchPath(qp)==='email'; }
 // Origin of a first-ever landing that carries no campaign identifier at all.
 // Never invents a channel: an unreadable/!external referrer yields 'unknown'.
 function originFromReferrer(){
   var r=''; try{ r=d.referrer||''; }catch(_){ }
   if(!r) return 'direct';
   try{
     var h=new URL(r).hostname.replace(/^www\./,'');
     var here=location.hostname.replace(/^www\./,'');
     if(h===here) return '';                                  // internal nav, not an origin
     if(/(^|\.)(google|bing|duckduckgo|yahoo|ecosia|brave|baidu|yandex)\./.test('.'+h)) return 'organic';
     return 'referral';
   }catch(_){ return 'unknown'; }
 }
 function readLS(k){ try{ return JSON.parse(localStorage.getItem(k)||'{}')||{}; }catch(_){ return {}; } }
 function writeLS(k,v){ try{ localStorage.setItem(k,JSON.stringify(v)); }catch(_){ } }
 function vlnAttr(){try{
   var qp=new URLSearchParams(location.search);
   var ck=function(n){var m=document.cookie.match(new RegExp('(?:^|; )'+n+'=([^;]+)'));return m?decodeURIComponent(m[1]):'';};
   var acq=readLS(ACQ_LS), ret=readLS(RET_LS);
   var ownedPath=ownedTouchPath(qp);
   var hasTouch=VLN_TOUCH.some(function(k){return !!qp.get(k)});

   if(ownedPath){
     // RETURN TOUCH ONLY. Acquisition is not read from, written to, or cleared.
     var dflt = ownedPath==='guide' ? 'veluna_guide' : 'veluna_email';
     ret.vln_return_source   = qpGet(qp,'utm_source')   || dflt;
     ret.vln_return_medium   = qpGet(qp,'utm_medium')   || ownedPath;
     ret.vln_return_campaign = qpGet(qp,'utm_campaign') || '';
     ret.vln_return_content  = qpGet(qp,'utm_content')  || '';
     ret.vln_return_path     = ownedPath;
     ret.vln_return_at       = new Date().toISOString();
     writeLS(RET_LS,ret);
   } else if(hasTouch){
     // A genuine new ACQUISITION touch replaces the acquisition set.
     var prev=acq; acq={};
     VLN_KEYS.forEach(function(k){var v=qpGet(qp,k); if(v)acq[k]=v;});
     var _c=qp.get('ttclid'); if(_c)acq.ttclid=_c;
     // vln_ts is the time of the CLICK, not of the last capture.
     var idKeys=['utm_campaign','utm_content','utm_term','vln_exp','vln_path','ttclid'];
     var same=idKeys.every(function(k){return (prev[k]||'')===(acq[k]||'')});
     acq.vln_ts=(same&&prev.vln_ts)?prev.vln_ts:new Date().toISOString();
     // _ttp identifies the DEVICE, not the click, so it survives a new paid touch.
     if(!acq.ttp&&prev.ttp)acq.ttp=prev.ttp;
     writeLS(ACQ_LS,acq);
   } else if(!Object.keys(acq).length){
     // First landing, no campaign identifier: record the origin we can actually
     // observe rather than leaving acquisition blank for email to fill in later.
     var origin=originFromReferrer();
     if(origin){ acq.vln_path=origin; acq.vln_ts=new Date().toISOString(); writeLS(ACQ_LS,acq); }
   }

   // ttclid/ttp belong to acquisition and are refreshed from cookies regardless of
   // which branch ran, so an email return can never strip them.
   var dirty=false;
   if(!acq.ttclid){ var _ck=ck('ttclid'); if(_ck){acq.ttclid=_ck; dirty=true;} }
   var _ttp=ck('_ttp'); if(_ttp && acq.ttp!==_ttp){ acq.ttp=_ttp; dirty=true; }
   if(dirty && Object.keys(acq).length) writeLS(ACQ_LS,acq);

   var out={}; var k2;
   for(k2 in acq){ if(acq[k2]) out[k2]=acq[k2]; }
   for(k2 in ret){ if(ret[k2]) out[k2]=ret[k2]; }
   if(!Object.keys(out).length)return;
   var sig=JSON.stringify(out), last='';
   try{ last=sessionStorage.getItem('__vln_attr_w')||''; }catch(_){}
   if(sig===last)return;
   fetch('/cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},credentials:'same-origin',keepalive:true,body:JSON.stringify({attributes:out})})
     .then(function(){try{sessionStorage.setItem('__vln_attr_w',sig);}catch(_){}}).catch(function(){});
 }catch(e){}}
 // QA/diagnostics surface: lets the browser test assert both namespaces directly.
 try{ w.VelunaAttribution={ acquisition:function(){return readLS(ACQ_LS)}, returnTouch:function(){return readLS(RET_LS)}, refresh:function(){return vlnAttr()} }; }catch(_){ }
 vlnAttr();
 setTimeout(vlnAttr,3000);
 setTimeout(vlnAttr,10000);
 try{
   var m=meta(),p=m.product;
   if(p&&path.indexOf('/products/')>-1){
     var v=(p.variants&&p.variants[0])||{},id=String(v.id||p.id||''),price=(v.price!=null?v.price/100:undefined),name=(d.title||p.type||'');
     ttq.track('ViewContent',{content_type:'product',content_id:id,content_name:name,value:price,currency:cur(),contents:[{content_id:id,content_type:'product',content_name:name,quantity:1,price:price}]});
   } else if(path.indexOf('quiz')>-1 && path.indexOf('my-ritual')<0){
     ttq.track('ViewContent',{content_type:'product',content_id:'ritual_quiz',content_name:'ritual_quiz',contents:[{content_id:'ritual_quiz',content_type:'product',content_name:'ritual_quiz',quantity:1}]});
   }
 }catch(e){}
 // AddToCart: fire once on a real cart mutation. Fast path = hook fetch+XHR; robust
 // fallback = a PerformanceObserver on /cart/add + a /cart.js delta read, because the
 // theme owns window.fetch (its cart handler overwrites ours) so the hooks alone miss adds.
 var __lastAtc=0, __ttCount=null;
 function emitAtc(contents,val){try{ if(!contents||!contents.length)return; var now=Date.now(); if(now-__lastAtc<1500)return; __lastAtc=now; ttq.track('AddToCart',{content_type:'product',content_id:contents[0].content_id,value:val,currency:cur(),contents:contents}); refreshCart(); vlnAttr(); }catch(e){}}
 function atc(data){try{
   var items=(data&&data.items)||(data&&(data.id||data.variant_id)?[data]:[]);
   if(!items.length)return;
   var contents=items.map(function(it){return {content_id:String(it.id||it.variant_id||it.product_id||''),content_type:'product',content_name:it.product_title||it.title||'',quantity:it.quantity||1,price:(it.price!=null?it.price/100:undefined)}});
   var val=contents.reduce(function(s,c){return s+((c.price||0)*(c.quantity||1))},0);
   emitAtc(contents,val);
 }catch(e){}}
 var of=w.fetch;
 if(of){w.fetch=function(){var a=arguments,u=(a[0]&&a[0].url)||a[0]||'',pr=of.apply(this,a);try{if(String(u).indexOf('/cart/add')>-1){pr.then(function(r){try{r.clone().json().then(atc).catch(function(){})}catch(e){}})}}catch(e){}return pr}}
 try{var xo=XMLHttpRequest.prototype.open,xs=XMLHttpRequest.prototype.send;
   XMLHttpRequest.prototype.open=function(mm,uu){this.__u=uu;return xo.apply(this,arguments)};
   XMLHttpRequest.prototype.send=function(){var x=this;try{if(String(x.__u||'').indexOf('/cart/add')>-1){x.addEventListener('load',function(){try{atc(JSON.parse(x.responseText))}catch(e){}})}}catch(e){}return xs.apply(this,arguments)};
 }catch(e){}
 try{
   var atcFromCart=function(){ fetch('/cart.js',{credentials:'same-origin'}).then(function(r){return r.json()}).then(function(c){ var prev=(typeof __ttCount==='number')?__ttCount:0; if(c.item_count>prev){ var it=(c.items||[])[(c.items||[]).length-1]; if(it){ emitAtc([{content_id:String(it.id||it.variant_id||''),content_type:'product',content_name:it.product_title||it.title||'',quantity:it.quantity||1,price:(it.price!=null?it.price/100:undefined)}], (it.price!=null?(it.price/100)*(it.quantity||1):undefined)); } } __ttCount=c.item_count; }).catch(function(){}); };
   fetch('/cart.js',{credentials:'same-origin'}).then(function(r){return r.json()}).then(function(c){__ttCount=c.item_count;}).catch(function(){});
   if(w.PerformanceObserver){ var __po=new PerformanceObserver(function(list){ list.getEntries().forEach(function(en){ if(String(en.name).indexOf('/cart/add')>-1){ setTimeout(atcFromCart,200); } }); }); __po.observe({entryTypes:['resource']}); }
 }catch(e){}
 // InitiateCheckout on checkout intent (uses cached cart for contents/value)
 d.addEventListener('click',function(e){try{
   var el=e.target&&e.target.closest&&e.target.closest('[name=checkout],button[name=checkout],a[href*="/checkout"]');
   if(!el)return;
   vlnAttr();   // last chance to persist a late _ttp before leaving the storefront
   var c=w.__ttCart,contents,val;
   if(c&&c.items){contents=c.items.map(function(it){return {content_id:String(it.id||it.variant_id||''),content_type:'product',content_name:it.product_title||it.title||'',quantity:it.quantity||1,price:(it.price!=null?it.price/100:undefined)}});val=(c.total_price!=null?c.total_price/100:undefined)}
   ttq.track('InitiateCheckout',{content_type:'product',currency:cur(),value:val,contents:contents});
 }catch(err){}},true);
 // CompleteRegistration on quiz completion
 try{if(location.href.indexOf('from_quiz=true')>-1||path.indexOf('/pages/my-ritual')>-1){
   ttq.track('CompleteRegistration',{content_type:'product',content_id:'ritual_quiz',content_name:'ritual_quiz'});
 }}catch(e){}
 // QUIZ FUNNEL (robust, DOM-state based): the quiz engine stops click propagation, so we
 // observe real state transitions, not clicks. Fires on genuine progress, never on page load.
 //  QuizStart    = first engagement (an answer becomes selected / interstitial / step>=2); NOT a load.
 //  QuizStep2/3/4= reaching the rendered question N (from the "STEP N OF 4" text).
 // Page load stays covered by ViewContent above.
 try{ if(path.indexOf('quiz')>-1 && path.indexOf('my-ritual')<0){
   var qStarted=false, qStepFired={};
   var qStepNow=function(){ var t=(d.body&&d.body.innerText)||'', re=/STEP\s*(\d+)\s*OF\s*(\d+)/ig, m, mx=0; while((m=re.exec(t))){ var n=parseInt(m[1],10); if(n>mx)mx=n; } return mx; };
   var qAnswered=function(){ try{ return !!d.querySelector('.veluna-quiz__option input:checked, .veluna-quiz__option[aria-checked="true"], .veluna-quiz__option.is-selected, .veluna-quiz__option [aria-checked="true"]'); }catch(e){ return false; } };
   var qStart=function(){ if(qStarted)return; qStarted=true; ttq.track('QuizStart',{content_type:'product',content_id:'ritual_quiz',content_name:'ritual_quiz',step:1}); };
   var qStepReached=function(n){ if(!n||n<2||qStepFired[n])return; qStepFired[n]=1; ttq.track('QuizStep'+n,{content_type:'product',content_id:'ritual_quiz_step_'+n,content_name:'ritual_quiz',step:n}); };
   var qScan=function(){ var s=qStepNow(); if(!qStarted && (s>=2 || qAnswered())) qStart(); qStepReached(s); };
   var qdeb; var qmo=new MutationObserver(function(){clearTimeout(qdeb);qdeb=setTimeout(qScan,120);});
   if(d.body)qmo.observe(d.body,{subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['aria-checked','checked','class','value']});
 }}catch(e){}
 // EMAIL MATCH (EMQ boost): when the visitor submits a valid email anywhere (quiz guide form, popup),
 // identify them (TikTok SDK hashes the email) so this + all later events match to a real user, and
 // fire a dedicated Subscribe conversion carrying the email = high match quality.
 try{
   var __eDone=false;
   var __eOK=function(v){return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((v||'').trim());};
   var __eCap=function(v){ v=(v||'').trim().toLowerCase(); if(!v||__eDone||!__eOK(v))return; __eDone=true;
     /* privacy 2026-09-06: email identifier NOT sent to ad platforms for the wellness quiz; the conversion event below still fires */
     try{ttq.track('Subscribe',{content_type:'product',content_id:'ritual_quiz',content_name:'ritual_quiz'});}catch(_){}
     try{ fetch('/cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},credentials:'same-origin',body:JSON.stringify({attributes:{email:v}})}).catch(function(){}); }catch(_){}
   };
   var __eEvt=function(e){var t=e.target; if(t&&(t.type==='email'||__eOK(t.value)))__eCap(t.value);};
   d.addEventListener('change',__eEvt,true); d.addEventListener('blur',__eEvt,true);
   d.addEventListener('submit',function(e){try{var f=e.target,ins=(f&&f.querySelectorAll)?f.querySelectorAll('input'):[];for(var i=0;i<ins.length;i++){if(ins[i].type==='email'||__eOK(ins[i].value))__eCap(ins[i].value);}}catch(_){}} ,true);
 }catch(e){}
})(window,document);
}catch(e){}
try{
(function(w,d){
 function whenFbq(cb){ if(w.fbq){cb();return;} var n=0,iv=setInterval(function(){ if(w.fbq){clearInterval(iv);cb();} else if(n++>60){clearInterval(iv);} },100); }
 var path=location.pathname;
 whenFbq(function(){
  try{
   // QUIZ FUNNEL (robust, DOM-state based): the quiz engine stops click propagation, so we
   // observe real state transitions, not clicks. Fires on genuine progress, never on page load.
   //  QuizStart    = first engagement (an answer becomes selected / interstitial / step>=2); NOT a load.
   //  QuizStep2/3/4= reaching the rendered question N (from the "STEP N OF 4" text).
   if(path.indexOf('quiz')>-1 && path.indexOf('my-ritual')<0){
     var qStarted=false, qStepFired={};
     var qStepNow=function(){ var t=(d.body&&d.body.innerText)||'', re=/STEP\s*(\d+)\s*OF\s*(\d+)/ig, m, mx=0; while((m=re.exec(t))){ var n=parseInt(m[1],10); if(n>mx)mx=n; } return mx; };
     var qAnswered=function(){ try{ return !!d.querySelector('.veluna-quiz__option input:checked, .veluna-quiz__option[aria-checked="true"], .veluna-quiz__option.is-selected, .veluna-quiz__option [aria-checked="true"]'); }catch(e){ return false; } };
     var qStart=function(){ if(qStarted)return; qStarted=true; w.fbq('trackCustom','QuizStart',{content_name:'ritual_quiz',step:1}); };
     var qStepReached=function(n){ if(!n||n<2||qStepFired[n])return; qStepFired[n]=1; w.fbq('trackCustom','QuizStep'+n,{content_name:'ritual_quiz',step:n}); };
     var qScan=function(){ var s=qStepNow(); if(!qStarted && (s>=2 || qAnswered())) qStart(); qStepReached(s); };
     var qdeb; var qmo=new MutationObserver(function(){clearTimeout(qdeb);qdeb=setTimeout(qScan,120);});
     if(d.body)qmo.observe(d.body,{subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['aria-checked','checked','class','value']});
   }
   // Quiz completion (result page)
   if(location.href.indexOf('from_quiz=true')>-1||path.indexOf('/pages/my-ritual')>-1){
     w.fbq('track','CompleteRegistration',{content_name:'ritual_quiz'});
   }
   // EMAIL MATCH (EMQ boost): when the visitor submits a valid email, add it to Advanced Matching
   // (fbq hashes it) so all subsequent events match to a real user, and fire a Lead carrying the email.
   var __eDone=false;
   var __eOK=function(v){return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((v||'').trim());};
   var __eCap=function(v){ v=(v||'').trim().toLowerCase(); if(!v||__eDone||!__eOK(v))return; __eDone=true;
     /* privacy 2026-09-06: email identifier NOT sent to ad platforms for the wellness quiz; the conversion event below still fires */
     try{w.fbq('track','Lead',{content_name:'ritual_quiz'});}catch(_){}
   };
   var __eEvt=function(e){var t=e.target; if(t&&(t.type==='email'||__eOK(t.value)))__eCap(t.value);};
   d.addEventListener('change',__eEvt,true); d.addEventListener('blur',__eEvt,true);
   d.addEventListener('submit',function(e){try{var f=e.target,ins=(f&&f.querySelectorAll)?f.querySelectorAll('input'):[];for(var i=0;i<ins.length;i++){if(ins[i].type==='email'||__eOK(ins[i].value))__eCap(ins[i].value);}}catch(_){}} ,true);
  }catch(e){}
 });
})(window,document);
}catch(e){}
})();
/* ===== VELUNA_QUIZ_FUNNEL_END ===== */
